import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { WHATSAPP_TEMPLATE_EDIT_FLOW_NAME } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/constants';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  /** When true, skip mappings-form validation (flow finished without a mappings UI). */
  fromFlowComplete?: boolean;
};

const EditInit = ({ workspaceId, whatsappConnectorFlowId, connectorInstanceId, whatsappConnectorFlow }) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const history = useHistory();

  // Fetch the prepare data when we have the required IDs
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingData, setMappingData] = useState<FlowFormData>({});

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const [createFlowInstance, { isLoading: isCreatingFlowInstance }] = useCreateFlowInstanceMutation();

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: whatsappConnectorFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      // const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!whatsappConnectorFlowId && !!audienceId) {
      fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, whatsappConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  const handleEditTemplate = async (opts?: CreateTemplateOpts) => {
    const instance = instances?.find((inst) => inst.connectorId === whatsappConnectorFlow?.connectorId);

    if (!instance) {
      const message = String(t('campaign:noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('campaign:missingAudience.toast'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: whatsappConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: String(t('campaign:whatsappTemplates.templateEditedSuccessfully')),
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.whatsappTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('campaign:flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const multiStepFlowFormProps = {
    id: 'template-edit-workaround-form',
    showTemplatePreview: true,
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: whatsappConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData) => {
      setFormData(allParams);
      setMappingData({});
      handleEditTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
    },
  };

  const isConnectorReady = !!connectorInstanceId && !!whatsappConnectorFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;

  if (isAudienceLoading || isPreparePending) {
    return <Loading />;
  }

  if (isAudienceSettled && !hasAudience) {
    return <AudienceRequiredEmptyState />;
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.editTemplatesHub')}</h2>
      </div>
      <ErrorBoundary>{prepareResult ? <MultiStepFlowForm {...multiStepFlowFormProps} isInAction={isCreatingFlowInstance} /> : null}</ErrorBoundary>
    </div>
  );
};

export const WhatsappTemplateEditWorkaround = () => {
  const { id: workspaceId } = useWorkspace();

  // Call connectorflow endpoint and parse connector_instance_id and whatsapp_connector_id
  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const whatsappConnectorFlow = connectorFlows?.find((flow) => flow.name === WHATSAPP_TEMPLATE_EDIT_FLOW_NAME);
  const whatsappConnectorFlowId = whatsappConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === whatsappConnectorFlow?.connectorId)?.id;

  if (isConnectorInstancesLoading || isConnectorFlowsLoading) {
    return <Loading />;
  }

  return (
    <EditInit
      workspaceId={workspaceId}
      whatsappConnectorFlowId={whatsappConnectorFlowId}
      connectorInstanceId={connectorInstanceId}
      whatsappConnectorFlow={whatsappConnectorFlow}
    />
  );
};
